import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, Clock, ArrowLeft, Activity, Apple, Moon, Heart, ChevronRight, BarChart3 } from "lucide-react";

const CATEGORY_META = {
  "edzés": { icon: Activity, color: "#00f260", label: "Edzés" },
  "táplálkozás": { icon: Apple, color: "#ffc107", label: "Táplálkozás" },
  "regeneráció": { icon: Moon, color: "#64b5f6", label: "Regeneráció" },
  "longevity": { icon: Heart, color: "#e040fb", label: "Longevity" },
};

const DIFFICULTY_COLORS = {
  "kezdő": "bg-[#00f260]/10 text-[#00f260]",
  "közepes": "bg-[#ffc107]/10 text-[#ffc107]",
  "haladó": "bg-[#ff5252]/10 text-[#ff5252]",
};

export default function AcademyPage() {
  const { api } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    api().get("/academy").then(r => {
      setArticles(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api]);

  const filtered = activeCategory === "all"
    ? articles
    : articles.filter(a => a.category === activeCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Article Detail View
  if (selectedArticle) {
    const cat = CATEGORY_META[selectedArticle.category] || CATEGORY_META["edzés"];
    const CatIcon = cat.icon;
    return (
      <div className="space-y-6 md:pt-20 max-w-3xl mx-auto" data-testid="academy-article-detail">
        <button
          data-testid="academy-back-btn"
          onClick={() => setSelectedArticle(null)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft size={18} /> Vissza
        </button>

        <div className="fade-in-up">
          <div className="flex items-center gap-2 mb-3">
            <Badge className={`${DIFFICULTY_COLORS[selectedArticle.difficulty]} border-0 text-xs`}>
              {selectedArticle.difficulty}
            </Badge>
            <Badge className="bg-white/5 border-0 text-gray-400 text-xs">
              <Clock size={10} className="mr-1" />{selectedArticle.duration_min} perc
            </Badge>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
            {selectedArticle.title}
          </h1>
          <div className="flex items-center gap-2 mb-6">
            <CatIcon size={16} style={{ color: cat.color }} />
            <span className="text-sm" style={{ color: cat.color }}>{cat.label}</span>
          </div>
        </div>

        <GlassCard className="fade-in-up fade-in-up-1" testId="article-content">
          <div className="prose prose-invert prose-sm max-w-none">
            {selectedArticle.content?.split("\n").map((line, i) => {
              if (line.startsWith("## ")) return <h2 key={i} className="font-heading text-xl font-bold text-[#00f260] mt-6 mb-3">{line.replace("## ", "")}</h2>;
              if (line.startsWith("### ")) return <h3 key={i} className="font-heading text-lg font-semibold text-white mt-4 mb-2">{line.replace("### ", "")}</h3>;
              if (line.startsWith("- ")) return <li key={i} className="text-gray-300 text-sm ml-4 mb-1">{line.replace("- ", "")}</li>;
              if (line.trim() === "") return <div key={i} className="h-2" />;
              return <p key={i} className="text-gray-300 text-sm leading-relaxed mb-2">{line}</p>;
            })}
          </div>
        </GlassCard>

        {selectedArticle.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 fade-in-up fade-in-up-2">
            {selectedArticle.tags.map(tag => (
              <span key={tag} className="px-3 py-1 rounded-full bg-white/5 text-xs text-gray-400">#{tag}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Article List View
  return (
    <div className="space-y-6 md:pt-20" data-testid="academy-page">
      <div className="flex items-center justify-between fade-in-up">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Academy</h1>
          <p className="text-sm text-gray-400">Tudásbázis a teljesítmény optimalizáláshoz</p>
        </div>
        <BookOpen className="text-[#00f260]" size={24} />
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 fade-in-up">
        <CategoryBtn active={activeCategory === "all"} onClick={() => setActiveCategory("all")} label="Összes" icon={BarChart3} color="#fff" />
        {Object.entries(CATEGORY_META).map(([key, meta]) => (
          <CategoryBtn key={key} active={activeCategory === key} onClick={() => setActiveCategory(key)} label={meta.label} icon={meta.icon} color={meta.color} />
        ))}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 fade-in-up fade-in-up-1">
        <GlassCard className="text-center py-3">
          <p className="font-heading text-2xl font-bold text-white">{articles.length}</p>
          <p className="text-[10px] text-gray-400 uppercase">Cikkek</p>
        </GlassCard>
        <GlassCard className="text-center py-3">
          <p className="font-heading text-2xl font-bold text-[#00f260]">4</p>
          <p className="text-[10px] text-gray-400 uppercase">Témakörök</p>
        </GlassCard>
        <GlassCard className="text-center py-3">
          <p className="font-heading text-2xl font-bold text-white">{articles.reduce((s, a) => s + (a.duration_min || 0), 0)}</p>
          <p className="text-[10px] text-gray-400 uppercase">Perc tartalom</p>
        </GlassCard>
      </div>

      {/* Article Cards */}
      <div className="space-y-4">
        {filtered.map((article, i) => {
          const cat = CATEGORY_META[article.category] || CATEGORY_META["edzés"];
          const CatIcon = cat.icon;
          return (
            <GlassCard
              key={article.id}
              testId={`academy-card-${i}`}
              className={`fade-in-up fade-in-up-${i + 2} cursor-pointer`}
              onClick={() => setSelectedArticle(article)}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}15` }}>
                  <CatIcon size={22} style={{ color: cat.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${DIFFICULTY_COLORS[article.difficulty]} border-0 text-[10px]`}>
                      {article.difficulty}
                    </Badge>
                    <span className="text-[10px] text-gray-500 flex items-center gap-1">
                      <Clock size={10} />{article.duration_min} perc
                    </span>
                  </div>
                  <h3 className="text-base text-white font-semibold">{article.title}</h3>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-2">{article.description}</p>
                </div>
                <ChevronRight size={18} className="text-gray-500 flex-shrink-0 mt-2" />
              </div>
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
}

function CategoryBtn({ active, onClick, label, icon: Icon, color }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs whitespace-nowrap transition-all ${
        active ? "bg-[#00f260] text-black font-medium" : "bg-white/5 text-gray-400 hover:bg-white/10"
      }`}
    >
      <Icon size={14} style={active ? {} : { color }} />
      {label}
    </button>
  );
}
